import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Divider,
} from '@mui/material';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  AccountCircle as AdminIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface AuditLog {
  _id: string;
  entityType: 'user' | 'challan';
  entityId: string;
  action: string;
  changes: Record<string, { old: any; new: any }>;
  adminUser: string;
  reason?: string;
  timestamp: string;
  createdAt: string;
}

interface AuditLogResponse {
  data: AuditLog[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  };
}

function AuditLogs() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState('');

  useEffect(() => {
    fetchAuditLogs();
  }, [page, rowsPerPage, entityTypeFilter, adminFilter]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (entityTypeFilter) params.entityType = entityTypeFilter;
      if (adminFilter) params.adminUser = adminFilter;

      const response = await api.get('/audit-logs', { params });
      const data: AuditLogResponse = response.data;
      setLogs(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatChanges = (changes: Record<string, { old: any; new: any }> | null | undefined) => {
    if (!changes) return [];
    return Object.entries(changes).map(([field, change]) => ({
      field,
      old: typeof change.old === 'object' ? JSON.stringify(change.old) : String(change.old),
      new: typeof change.new === 'object' ? JSON.stringify(change.new) : String(change.new)
    }));
  };

  const clearFilters = () => {
    setEntityTypeFilter('');
    setAdminFilter('');
    setPage(0);
  };

  const getEntityIcon = (entityType: string) => {
    return entityType === 'user' ? <PersonIcon /> : <ReceiptIcon />;
  };

  const getEntityColor = (entityType: string) => {
    return entityType === 'user' ? 'primary' : 'secondary';
  };

  // Get unique admin users for filter
  const uniqueAdmins = Array.from(new Set(logs.map(log => log.adminUser)));

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <HistoryIcon sx={{ mr: 2, fontSize: 36, color: '#059669' }} />
        <Box>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 500, color: '#059669' }}>
            Audit Logs
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 400, mt: 0.25 }}>
            Complete history of all changes made to users and challans
          </Typography>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <Card sx={{ flex: '1 1 200px', borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <Box sx={{ height: 3, background: 'linear-gradient(90deg,#059669,#fbbf24)' }} />
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Total Changes
                </Typography>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 500, color: '#1e293b', lineHeight: 1 }}>
                  {total.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(5,150,105,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <HistoryIcon sx={{ fontSize: 20, color: '#059669' }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <Box sx={{ height: 3, background: 'linear-gradient(90deg,#fbbf24,#059669)' }} />
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  User Changes
                </Typography>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 500, color: '#1e293b', lineHeight: 1 }}>
                  {logs.filter(log => log.entityType === 'user').length}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 400, mt: 0.5 }}>
                  Current page
                </Typography>
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(251,191,36,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PersonIcon sx={{ fontSize: 20, color: '#fbbf24' }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <Box sx={{ height: 3, background: 'linear-gradient(90deg,#059669,#10b981)' }} />
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Challan Changes
                </Typography>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 500, color: '#1e293b', lineHeight: 1 }}>
                  {logs.filter(log => log.entityType === 'challan').length}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 400, mt: 0.5 }}>
                  Current page
                </Typography>
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(5,150,105,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ReceiptIcon sx={{ fontSize: 20, color: '#059669' }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3, borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterIcon sx={{ mr: 1, color: '#059669' }} />
          <Typography sx={{ fontWeight: 500, fontSize: '0.95rem', color: '#059669' }}>Filters</Typography>
        </Box>
        
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Entity Type</InputLabel>
            <Select
              value={entityTypeFilter}
              label="Entity Type"
              onChange={(e) => setEntityTypeFilter(e.target.value as string)}
              sx={{ borderRadius: '12px' }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="user">Users</MenuItem>
              <MenuItem value="challan">Challans</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Admin User</InputLabel>
            <Select
              value={adminFilter}
              label="Admin User"
              onChange={(e) => setAdminFilter(e.target.value as string)}
              sx={{ borderRadius: '12px' }}
            >
              <MenuItem value="">All</MenuItem>
              {uniqueAdmins.map((admin) => (
                <MenuItem key={admin} value={admin}>
                  {admin}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(entityTypeFilter || adminFilter) && (
            <Chip
              label="Clear Filters"
              onClick={clearFilters}
              onDelete={clearFilters}
              sx={{ bgcolor: '#059669', color: 'white', fontWeight: 500 }}
            />
          )}
        </Box>
      </Card>

      {/* Audit Logs Table */}
      <Card sx={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {['Timestamp', 'Entity', 'Action', 'Admin User', 'Changes', 'Reason'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 500, fontSize: '0.75rem', color: '#059669', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e2e8f0', py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log._id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(log.timestamp)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getEntityIcon(log.entityType)}
                        <Box ml={1}>
                          <Chip
                            label={log.entityType}
                            color={getEntityColor(log.entityType) as any}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            ID: {log.entityId.slice(-8)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        label={log.action}
                        color="warning"
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <AdminIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight="medium">
                          {log.adminUser}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        {formatChanges(log.changes).map((change, index) => (
                          <Box key={index} mb={1}>
                            <Typography variant="body2" fontWeight="medium" color="primary">
                              {change.field}:
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={change.old || 'null'}
                                size="small"
                                variant="outlined"
                                color="error"
                                sx={{ fontSize: '0.7rem', maxWidth: 120 }}
                              />
                              <Typography variant="body2">→</Typography>
                              <Chip
                                label={change.new || 'null'}
                                size="small"
                                variant="outlined"
                                color="success"
                                sx={{ fontSize: '0.7rem', maxWidth: 120 }}
                              />
                            </Stack>
                          </Box>
                        ))}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', maxWidth: 200 }}>
                        {log.reason || 'No reason provided'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Card>
    </Box>
  );
}

export default AuditLogs; 